import { toggleMobile } from './mobile';

/**
 * Initializes the menu using event delegation.
 * This approach is more robust against DOM changes and script execution delays
 * caused by Cookiebot or other late-loading scripts.
 *
 * We use a capturing listener or check the path to ensure we catch events
 * even if they are stopped by other scripts, and we use closest() to find
 * our triggers.
 */
export function openMenu() {
	document.addEventListener('click', (event) => {
		const target = event.target as HTMLElement;
		if (!target) return;

		// 1. Hamburger Menu Toggle
		const hamburger = target.closest('.hamburger');
		if (hamburger) {
			const mobileMenu = document.getElementById('mobile-menu');
			if (mobileMenu) {
				toggleMobile(mobileMenu);
			}
			return;
		}

		// 2. Mega Menu Top Level Toggle
		// Matches .menu-link elements with data-menu-open that are in the primary navigation.
		const topLevelLink = target.closest('.menu-link[data-menu-open]');
		if (topLevelLink instanceof HTMLElement && !topLevelLink.closest('#mega-menu')) {
			const id = topLevelLink.dataset.menuOpen;
			if (id) {
				// Prevent default if it's a toggle button/link
				if (
					topLevelLink.tagName === 'BUTTON' ||
					topLevelLink.getAttribute('href') === '#'
				) {
					event.preventDefault();
				}
				toggleMenu(id);
			}
			return;
		}

		// 3. Mega Menu Sub-links (Internal navigation within the mega menu)
		// Matches elements with data-menu-open inside the mega menu dropdown.
		const subMenuLink = target.closest('#mega-menu [data-menu-open]');
		if (subMenuLink instanceof HTMLElement) {
			const id = subMenuLink.dataset.menuOpen;
			if (id) {
				event.preventDefault();
				showMenu(id);
			}
			return;
		}
	});

	// 4. Hover functionality for Mega Menu columns
	// Using mouseover with delegation for the secondary level content
	document.addEventListener('mouseover', (event) => {
		const target = event.target as HTMLElement;
		const hoverLink = target.closest('#mega-menu [data-hover-open]');
		if (hoverLink instanceof HTMLElement) {
			const id = hoverLink.dataset.hoverOpen;
			if (id) {
				showMenu(id);
			}
		}
	});
}

/**
 * Toggles the mega menu visibility based on the provided ID.
 *
 * @param {string} id - The ID of the menu to toggle.
 */
function toggleMenu(id: string) {
	const menuContainer = document.getElementById('mega-menu');
	const menuFirstColumn = document.getElementById('first');

	if (menuContainer instanceof HTMLElement && menuFirstColumn instanceof HTMLElement) {
		const currentId = menuContainer.dataset.current;

		// If clicking the same one, close it. Otherwise open/switch.
		if (currentId === id) {
			// Close Menu
			menuContainer.dataset.current = '';
			menuContainer.classList.remove('open');
		} else {
			// Open Menu and populate first level
			populateMenu(menuFirstColumn, id);
			showMenu(''); // Clear/reset second column content
			menuContainer.dataset.current = id;
			menuContainer.classList.add('open');

			// Ensure navigation header has necessary classes if using jutils scroll logic
			const header = menuContainer.closest('.navigation-header');
			if (header) {
				header.classList.add('scrollTop'); // Force visibility
			}
		}
	}
}

/**
 * Shows the secondary menu level content.
 *
 * @param {string} id - The ID of the menu content to show.
 */
function showMenu(id: string) {
	const menuSecondColumn = document.getElementById('second');
	if (menuSecondColumn instanceof HTMLElement) {
		populateMenu(menuSecondColumn, id);
	}
}

/**
 * Populates an HTML element with content from a hidden template based on the provided ID.
 *
 * @param {HTMLElement} element - The container element to populate.
 * @param {string} menuId - The ID of the menu template.
 * @returns {string} The parent ID of the template, if defined.
 */
export function populateMenu(element: HTMLElement, menuId: string): string {
	if (!menuId) {
		element.innerHTML = '';
		return '';
	}

	const templateId = `template-${menuId}`;
	const template = document.getElementById(templateId);

	if (template) {
		element.innerHTML = template.innerHTML;
		return template.dataset.parent ?? '';
	}

	element.innerHTML = '';
	return '';
}
